import useSWR from 'swr';
import { backendClient } from '@/lib/api-client';
import { format } from 'date-fns';
import type { MatterMember } from '@/types/base';

const fetcher = async (url: string) => {
  const response = await backendClient.get(url);
  return response.data;
};

export interface TimeSeriesPoint {
  date: string;
  value: number;
}

export interface TokenTimeSeriesPoint {
  date: string;
  document_tokens: number;
  chat_tokens: number;
  eca_tokens: number;
  total: number;
}

export interface MessageTimeSeriesPoint {
  date: string;
  user_messages: number;
  assistant_messages: number;
  total: number;
}

export interface UserUsageItem {
  user_id: number;
  user_guid: string;
  user_name: string;
  user_email: string;
  total_tokens: number;
  document_tokens: number;
  chat_tokens: number;
  eca_tokens: number;
  documents_uploaded: number;
  messages_sent: number;
  last_activity_date: string | null;
  active_days: number;
}

export interface MatterUsageItem {
  matter_id: number;
  matter_guid: string;
  matter_name: string;
  case_reference: string | null;
  documents_count: number;
  messages_count: number;
  users_count: number;
}

export type Granularity = 'day' | 'week' | 'month';

export interface AnalyticsOverview {
  total_documents: number;
  total_tokens_used: number;
  total_matters: number;
  total_messages: number;
  total_users: number;
  documents_over_time: TimeSeriesPoint[];
  tokens_over_time: TokenTimeSeriesPoint[];
  messages_over_time: MessageTimeSeriesPoint[];
  active_users_over_time: TimeSeriesPoint[];
}

export interface AnalyticsFilters {
  dateFrom: Date | null;
  dateTo: Date | null;
  matterGuid: string | null;
  userGuid: string | null;
  granularity: Granularity;
}

/**
 * Hook to fetch analytics overview data with optional filters.
 */
export const useAnalyticsOverview = (filters: AnalyticsFilters) => {
  // Build query string
  const params = new URLSearchParams();
  if (filters.dateFrom) {
    params.append('date_from', format(filters.dateFrom, 'yyyy-MM-dd'));
  }
  if (filters.dateTo) {
    params.append('date_to', format(filters.dateTo, 'yyyy-MM-dd'));
  }
  if (filters.matterGuid) {
    params.append('matter_guid', filters.matterGuid);
  }
  if (filters.userGuid) {
    params.append('user_guid', filters.userGuid);
  }
  if (filters.granularity) {
    params.append('granularity', filters.granularity);
  }

  const queryString = params.toString();
  const url = `/analytics/overview${queryString ? `?${queryString}` : ''}`;

  const { data, isLoading, error, mutate } = useSWR<AnalyticsOverview>(url, fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 60000, // 1 minute
  });

  return {
    data,
    isLoading,
    error,
    refresh: mutate,
  };
};

export interface UserAnalyticsItem {
  user_id: number;
  user_guid: string;
  user_name: string;
  user_email: string;
  is_active: boolean;
  position: string | null;
  role_name: string | null;
  total_tokens: number;
  document_tokens: number;
  chat_tokens: number;
  eca_tokens: number;
  documents_uploaded: number;
  messages_sent: number;
  matters_count: number;
  last_activity_date: string | null;
  active_days: number;
}

export interface UserAnalyticsResponse {
  users: UserAnalyticsItem[];
  total_count: number;
  page: number;
  page_size: number;
}

export type UserSortField = 'name' | 'total_tokens' | 'documents_uploaded' | 'messages_sent' | 'last_activity' | 'active_days';
export type MatterSortField = 'name' | 'documents_count' | 'messages_count' | 'users_count';
export type SortOrder = 'asc' | 'desc';

/**
 * Hook to fetch all user analytics (client-side pagination/sorting).
 */
export const useUserAnalytics = (
  filters: Omit<AnalyticsFilters, 'matterGuid' | 'userGuid'>
) => {
  const params = new URLSearchParams();
  if (filters.dateFrom) {
    params.append('date_from', format(filters.dateFrom, 'yyyy-MM-dd'));
  }
  if (filters.dateTo) {
    params.append('date_to', format(filters.dateTo, 'yyyy-MM-dd'));
  }
  const url = `/analytics/users?${params.toString()}`;

  const { data, isLoading, error, mutate } = useSWR<UserAnalyticsResponse>(url, fetcher, {
    revalidateOnFocus: false,
  });

  return {
    data,
    isLoading,
    error,
    refresh: mutate,
  };
};

export interface MatterAnalyticsItem {
  matter_id: number;
  matter_guid: string;
  matter_name: string;
  matter_no: string | null;
  case_reference: string | null;
  region: string | null;
  forum: string | null;
  is_active: boolean;
  created_at: string | null;
  documents_count: number;
  messages_count: number;
  users_count: number;
  total_tokens: number;
  members: MatterMember[];
}

export interface MatterAnalyticsResponse {
  matters: MatterAnalyticsItem[];
  total_count: number;
  page: number;
  page_size: number;
}

/**
 * Hook to fetch all matter analytics (client-side pagination/sorting).
 */
export const useMatterAnalytics = (
  filters: Omit<AnalyticsFilters, 'matterGuid' | 'userGuid'>
) => {
  const params = new URLSearchParams();
  if (filters.dateFrom) {
    params.append('date_from', format(filters.dateFrom, 'yyyy-MM-dd'));
  }
  if (filters.dateTo) {
    params.append('date_to', format(filters.dateTo, 'yyyy-MM-dd'));
  }
  const url = `/analytics/matters?${params.toString()}`;

  const { data, isLoading, error, mutate } = useSWR<MatterAnalyticsResponse>(url, fetcher, {
    revalidateOnFocus: false,
  });

  return {
    data,
    isLoading,
    error,
    refresh: mutate,
  };
};

export interface AnalyticsMatter {
  guid: string;
  name: string;
}

/**
 * Hook to fetch all matters for the analytics filter dropdown (admin only).
 */
export const useAllMattersForAnalytics = () => {
  const { data, isLoading, error } = useSWR<AnalyticsMatter[]>(
    '/analytics/all-matters',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    matters: data ?? [],
    isLoading,
    error,
  };
};

export interface PromptAnalyticsItem {
  message_id: number;
  user_guid: string;
  user_name: string;
  user_email: string;
  matter_guid: string;
  matter_name: string;
  case_reference: string | null;
  content: string;
  content_preview: string;
  token_count: number | null;
  created_at: string;
  mode: string | null;
}

export interface PromptsAnalyticsResponse {
  prompts: PromptAnalyticsItem[];
  total_count: number;
  page: number;
  page_size: number;
  feature_enabled: boolean;
}

export type PromptSortField = 'created_at' | 'user_name' | 'matter_name' | 'token_count' | 'content_length';

/**
 * Hook to fetch user prompts with filtering, pagination, and sorting (admin only).
 */
export const usePromptsAnalytics = (
  filters: AnalyticsFilters,
  page: number = 1,
  pageSize: number = 20,
  sortBy: PromptSortField = 'created_at',
  sortOrder: SortOrder = 'desc'
) => {
  const params = new URLSearchParams();

  if (filters.dateFrom) {
    params.append('date_from', format(filters.dateFrom, 'yyyy-MM-dd'));
  }
  if (filters.dateTo) {
    params.append('date_to', format(filters.dateTo, 'yyyy-MM-dd'));
  }
  if (filters.matterGuid) {
    params.append('matter_guid', filters.matterGuid);
  }
  if (filters.userGuid) {
    params.append('user_guid', filters.userGuid);
  }

  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  params.append('sort_by', sortBy);
  params.append('sort_order', sortOrder);

  const url = `/analytics/prompts?${params.toString()}`;

  const { data, isLoading, error, mutate } = useSWR<PromptsAnalyticsResponse>(
    url,
    fetcher,
    {
      revalidateOnFocus: false,
      keepPreviousData: true, // Important for smooth pagination
    }
  );

  return {
    data,
    isLoading,
    error,
    refresh: mutate,
  };
};
